import { formatCurrency } from "../../include/money";
import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { indiaDate, indiaDateTime } from "../../include/indiaDate";

const numberColors = ["#fde047", "#fb7185", "#34d399", "#60a5fa", "#c084fc", "#fb923c", "#f9a8d4", "#67e8f9", "#facc15", "#ffffff"];

function nameCase(value) {
  return String(value || "").trim().toLowerCase().replace(/(^|\s|-)([a-z])/g, (_, sep, letter) => `${sep}${letter.toUpperCase()}`);
}

export default function LuckyDrawWinner() {
  const navigate = useNavigate();
  const location = useLocation();
  const draw = location.state || {};
  const winner = draw.winner;
  const isAdmin = location.pathname.startsWith("/console");
  const isClub = location.pathname.startsWith("/club");
  const winnersPath = draw.draw_id
    ? (isAdmin ? `/console/luckydraw/${draw.draw_id}?view=winners` : isClub ? `/club/luckydraw/${draw.draw_id}?view=winners` : `/luckydraw/${draw.draw_id}?view=winners`)
    : (isAdmin ? "/console/luckydraw" : isClub ? "/club/luckydraw" : "/luckydraws");
  const [count, setCount] = useState(1);
  const [phase, setPhase] = useState(winner ? "count" : "empty");

  useEffect(() => {
    if (!winner || phase !== "count") return undefined;
    if (count >= 10) {
      const reveal = setTimeout(() => setPhase("winner"), 700);
      return () => clearTimeout(reveal);
    }
    const step = setTimeout(() => setCount((value) => value + 1), 800);
    return () => clearTimeout(step);
  }, [count, phase, winner]);

  const back = () => {
    navigate(winnersPath);
  };

  return (
    <Box sx={{
      minHeight: "calc(100dvh - 72px)",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 2,
      py: 3,
      background: "linear-gradient(145deg, #6d28d9 0%, #db2777 34%, #f59e0b 68%, #22c55e 100%)",
      backgroundSize: "180% 180%",
      animation: "cashi-shift 8s ease infinite",
    }}>
      <Confetti />
      {phase === "empty" && (
        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", color: "#fff", maxWidth: 480 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 800 }}>Issue a winner from the Lucky Draw Coupons result.</Typography>
          <ActionButton onClick={back}>Back</ActionButton>
        </Box>
      )}
      {phase === "count" && (
        <Box key={count} sx={{ position: "relative", zIndex: 1, textAlign: "center", color: "#fff", animation: "cashi-pop 0.45s ease" }}>
          <Typography sx={{ fontSize: 14, letterSpacing: 2.2, fontWeight: 800 }}>LUCKY DRAW</Typography>
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 700, mt: 1, opacity: 0.95 }}>{draw.title || "Today’s draw"}</Typography>
          <Typography sx={{
            fontSize: { xs: 140, sm: 200 },
            fontWeight: 900,
            lineHeight: 0.9,
            mt: 1,
            color: numberColors[(count - 1) % numberColors.length],
            textShadow: "0 10px 30px rgba(0,0,0,0.25)",
          }}>
            {count}
          </Typography>
          <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 650, mt: 1 }}>
            {count < 10 ? "The winner is on the way" : "Here we go"}
          </Typography>
        </Box>
      )}
      {phase === "winner" && winner && (
        <Box sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 560, textAlign: "center", color: "#fff", animation: "cashi-pop 0.55s ease" }}>
          <Typography sx={{ fontSize: 13, letterSpacing: 2, fontWeight: 800 }}>WINNER ANNOUNCED</Typography>
          <Typography sx={{ fontSize: { xs: 18, sm: 20 }, mt: 1, fontWeight: 650 }}>{draw.title || "Lucky Draw Coupons"}</Typography>
          <Box sx={{ width: 88, height: 88, mx: "auto", mt: 2, borderRadius: "50%", background: "rgba(255,255,255,0.18)", border: "3px solid rgba(255,255,255,0.75)", display: "grid", placeItems: "center", fontSize: 42 }}>★</Box>
          <Typography sx={{ fontSize: { xs: 34, sm: 46 }, fontWeight: 900, lineHeight: 1.05, mt: 1.5 }}>{nameCase(winner.customer_name)}</Typography>
          <Typography sx={{ fontSize: { xs: 16, sm: 18 }, mt: 1, maxWidth: 420, mx: "auto", lineHeight: 1.45 }}>
            {nameCase(winner.customer_name)} wins this {String(draw.frequency || "draw").toLowerCase()} draw.
          </Typography>
          <Box sx={{ mt: 2, display: "inline-flex", alignItems: "center", gap: 1, background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 10, px: 2, py: 0.8 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, opacity: 0.85 }}>Mobile</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 850 }}>{winner.customer_mobile || "—"}</Typography>
          </Box>
          <Box sx={{ mt: 2.2, background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.32)", borderRadius: 3, textAlign: "left", overflow: "hidden" }}>
            <Detail label="Email" value={winner.customer_email} />
            <Detail label="Store" value={winner.store_name} />
            <Detail label="Coupon" value={winner.coupon_code} />
            <Detail label="Bill" value={winner.order_amount ? formatCurrency(winner.order_amount) : ""} />
            <Detail label="When" value={draw.slot_label} />
            <Detail label="Frequency" value={draw.frequency} />
            <Detail label="Issued on" value={indiaDate(winner.date_added)} />
            <Detail label="Launch time" value={indiaDateTime(winner.winner_at || winner.date_added)} last />
          </Box>
          <ActionButton onClick={back}>Back to winners</ActionButton>
        </Box>
      )}
      <style>{`
        @keyframes cashi-fall {
          0% { transform: translateY(-24px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(280deg); opacity: 0.15; }
        }
        @keyframes cashi-pop {
          0% { transform: scale(0.72); opacity: 0; }
          70% { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes cashi-shift {
          0% { background-position: 0% 40%; }
          50% { background-position: 100% 60%; }
          100% { background-position: 0% 40%; }
        }
      `}</style>
    </Box>
  );
}

function Detail({ label, value, last }) {
  return (
    <Box sx={{ px: 2, py: 1.15, display: "flex", justifyContent: "space-between", gap: 2, borderBottom: last ? 0 : "1px solid rgba(255,255,255,0.18)" }}>
      <Typography sx={{ fontSize: 13, opacity: 0.8 }}>{label}</Typography>
      <Typography sx={{ fontSize: 14.5, fontWeight: 750, textAlign: "right" }}>{value || "—"}</Typography>
    </Box>
  );
}

function ActionButton({ children, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      marginTop: 22,
      minHeight: 48,
      minWidth: 180,
      padding: "0 22px",
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.7)",
      background: "#fff",
      color: "#6d28d9",
      fontWeight: 800,
      fontSize: 16,
      cursor: "pointer",
    }}>
      {children}
    </button>
  );
}

function Confetti() {
  const colors = ["#fde047", "#fb7185", "#34d399", "#60a5fa", "#c084fc", "#fb923c", "#fff"];
  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {colors.concat(colors, colors, colors).map((color, index) => (
        <Box key={index} sx={{
          position: "absolute",
          top: -20,
          left: `${(index * 13) % 100}%`,
          width: index % 3 === 0 ? 14 : 10,
          height: index % 2 === 0 ? 18 : 10,
          borderRadius: index % 2 ? "50%" : "2px",
          background: color,
          animation: `cashi-fall ${2.4 + (index % 5) * 0.35}s linear ${(index % 8) * 0.12}s infinite`,
        }} />
      ))}
    </Box>
  );
}
